import { useEffect, useMemo, useRef, useState } from 'react'
import { IconChevronDown, IconSearch } from '@tabler/icons-react'
import { matchesSearch } from '@webbuilder/shared-types'
import { useI18n } from '../../lib/i18n'

type Country = { code: string; dial: string; name: string; flag: string }

// Los tres mercados primero, luego América Latina y otros comunes.
const COUNTRIES: Country[] = [
  { code: 'US', dial: '+1', name: 'United States', flag: '🇺🇸' },
  { code: 'MX', dial: '+52', name: 'México', flag: '🇲🇽' },
  { code: 'ES', dial: '+34', name: 'España', flag: '🇪🇸' },
  { code: 'CO', dial: '+57', name: 'Colombia', flag: '🇨🇴' },
  { code: 'AR', dial: '+54', name: 'Argentina', flag: '🇦🇷' },
  { code: 'CL', dial: '+56', name: 'Chile', flag: '🇨🇱' },
  { code: 'PE', dial: '+51', name: 'Perú', flag: '🇵🇪' },
  { code: 'EC', dial: '+593', name: 'Ecuador', flag: '🇪🇨' },
  { code: 'GT', dial: '+502', name: 'Guatemala', flag: '🇬🇹' },
  { code: 'CR', dial: '+506', name: 'Costa Rica', flag: '🇨🇷' },
  { code: 'PA', dial: '+507', name: 'Panamá', flag: '🇵🇦' },
  { code: 'DO', dial: '+1', name: 'Rep. Dominicana', flag: '🇩🇴' },
  { code: 'VE', dial: '+58', name: 'Venezuela', flag: '🇻🇪' },
  { code: 'BO', dial: '+591', name: 'Bolivia', flag: '🇧🇴' },
  { code: 'UY', dial: '+598', name: 'Uruguay', flag: '🇺🇾' },
  { code: 'PY', dial: '+595', name: 'Paraguay', flag: '🇵🇾' },
  { code: 'HN', dial: '+504', name: 'Honduras', flag: '🇭🇳' },
  { code: 'SV', dial: '+503', name: 'El Salvador', flag: '🇸🇻' },
  { code: 'NI', dial: '+505', name: 'Nicaragua', flag: '🇳🇮' },
  { code: 'BR', dial: '+55', name: 'Brasil', flag: '🇧🇷' },
  { code: 'CA', dial: '+1', name: 'Canadá', flag: '🇨🇦' },
]

function parse(value: string): { country: Country; number: string } {
  const v = (value ?? '').trim()
  if (v.startsWith('+')) {
    // Longest dial-code match wins (e.g. +593 before +5).
    const match = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length).find((c) => v.startsWith(c.dial))
    if (match) return { country: match, number: v.slice(match.dial.length).trim() }
  }
  return { country: browserCountry(), number: v }
}

/**
 * El país que se propone sin número escrito: el del navegador (`en-US` → US).
 *
 * Arrancaba siempre en México, herencia de cuando el producto sólo miraba allí:
 * a quien lo abría en Estados Unidos le salía +52 en cada teléfono.
 */
function browserCountry(): Country {
  const tags = typeof navigator === 'undefined' ? [] : [...(navigator.languages ?? []), navigator.language]
  for (const tag of tags) {
    const region = tag?.split('-')[1]?.toUpperCase()
    const found = region ? COUNTRIES.find((c) => c.code === region) : undefined
    if (found) return found
  }
  return COUNTRIES.find((c) => c.code === 'US')!
}

/** El nombre del país en el idioma del panel; la lista guarda sólo el de respaldo. */
function countryName(country: Country, language: string): string {
  try {
    return new Intl.DisplayNames([language], { type: 'region' }).of(country.code) ?? country.name
  } catch {
    return country.name
  }
}

export function PhoneInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t, language } = useI18n()
  const initial = useMemo(() => parse(value), []) // eslint-disable-line react-hooks/exhaustive-deps
  const [country, setCountry] = useState<Country>(initial.country)
  const [number, setNumber] = useState(initial.number)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)

  function emit(c: Country, n: string) {
    onChange(n.trim() ? `${c.dial} ${n.trim()}` : '')
  }

  useEffect(() => {
    if (!open) return
    const onDoc = (e: PointerEvent) => { if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('pointerdown', onDoc)
    return () => document.removeEventListener('pointerdown', onDoc)
  }, [open])

  const filtered = query.trim()
    ? COUNTRIES.filter((c) => matchesSearch(`${countryName(c, language)} ${c.name} ${c.dial} ${c.code}`, query))
    : COUNTRIES

  return (
    <div className="cwa-phone" ref={rootRef}>
      <button type="button" className="cwa-phone-code" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open}>
        <span className="cwa-phone-flag">{country.flag}</span>
        <span className="cwa-phone-dial">{country.dial}</span>
        <IconChevronDown size={14} />
      </button>
      <input
        className="cwa-phone-num"
        type="tel"
        inputMode="tel"
        value={number}
        onChange={(e) => { const n = e.target.value.replace(/[^\d\s()-]/g, ''); setNumber(n); emit(country, n) }}
        placeholder="55 1234 5678"
      />

      {open ? (
        <div className="cwa-phone-menu" role="listbox">
          <div className="cwa-phone-search">
            <IconSearch size={14} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('createAi.searchCountry')} autoFocus />
          </div>
          <div className="cwa-phone-list">
            {filtered.map((c) => (
              <button
                key={c.code}
                type="button"
                className={`cwa-phone-opt${c.code === country.code ? ' active' : ''}`}
                onClick={() => { setCountry(c); setOpen(false); setQuery(''); emit(c, number) }}
              >
                <span className="cwa-phone-flag">{c.flag}</span>
                <span className="cwa-phone-opt-name">{countryName(c, language)}</span>
                <span className="cwa-phone-opt-dial">{c.dial}</span>
              </button>
            ))}
            {filtered.length === 0 ? <p className="cwa-img-empty">{t('createAi.noResults')}</p> : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}
